// components/cashSession/CloseSessionModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import { Unlock, AlertTriangle, CheckCircle2, Printer, DollarSign } from 'lucide-react';
import { cashSessionService } from '../../services/Cashsessionservice';

/**
 * Modal de fermeture de session.
 * Affiche les totaux théoriques et permet de saisir le comptage réel.
 *
 * Props:
 * - show: boolean
 * - session: CashSessionDto (avec les totaux live)
 * - onClose: () => void
 * - onSessionClosed: (closedSession) => void
 * - forceMode: boolean (default false) — si true, c'est le manager qui force la fermeture
 */
const CloseSessionModal = ({ show, session, onClose, onSessionClosed, forceMode = false }) => {
  const [closingAmount, setClosingAmount] = useState('');
  const [closingNote, setClosingNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [closedSession, setClosedSession] = useState(null);

  // Reset au changement de session
  useEffect(() => {
    if (show) {
      setClosingAmount('');
      setClosingNote('');
      setError(null);
      setStep('form');
      setClosedSession(null);
    }
  }, [show, session?.id]);

  if (!session) return null;

  const fmt = cashSessionService.formatCurrency;
  //const theoretical = (parseFloat(session.opening_amount) || 0) + (parseFloat(session.total_cash) || 0);
  const theoretical = (session.opening_amount || 0) 
                  + (session.total_cash || 0) 
                  - (session.total_expenses || 0);
  const realAmount = parseFloat(closingAmount) || 0;
  const difference = closingAmount ? realAmount - theoretical : null;

  const handleSubmit = async () => {
    setError(null);

    if (closingAmount === '' || isNaN(realAmount) || realAmount < 0) {
      setError('Veuillez saisir un montant valide (≥ 0)');
      return;
    }

    if (forceMode && !closingNote.trim()) {
      setError('Une note explicative est obligatoire pour forcer la fermeture');
      return;
    }

    setLoading(true);
    try {
      const response = forceMode
        ? await cashSessionService.forceCloseSession(session.id, realAmount, closingNote)
        : await cashSessionService.closeSession(session.id, realAmount, closingNote);

      if (response.success && response.data) {
        setClosedSession(response.data);
        setStep('success');
      } else {
        setError(response.message || 'Erreur lors de la fermeture');
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onSessionClosed?.(closedSession);
    onClose();
  };

  const handlePrint = () => {
    window.print();
  };

  // ═══════════════════════════════════════════════════════════
  // ÉTAPE 1 : Formulaire de fermeture
  // ═══════════════════════════════════════════════════════════
  if (step === 'form') {
    return (
      <Modal show={show} onHide={onClose} backdrop="static" centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Unlock size={20} className="text-warning" />
            {forceMode ? 'Forcer la fermeture' : 'Fermer ma session'}
            <Badge bg="secondary" className="ms-2">{session.session_number}</Badge>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {forceMode && (
            <Alert variant="warning" className="d-flex align-items-start gap-2 mb-3">
              <AlertTriangle size={20} className="flex-shrink-0 mt-1" />
              <div>
                <strong>Fermeture forcée par le gestionnaire</strong>
                <p className="mb-0 small">
                  Session de <strong>{session.user_name}</strong> verrouillée depuis plus de 24h.
                  Une note explicative est obligatoire.
                </p>
              </div>
            </Alert>
          )}

          {/* === Résumé de la session === */}
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <strong>📊 Résumé de la session</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={6}>
                  <small className="text-muted">Ouverture :</small>
                  <div>{cashSessionService.formatDateTime(session.opened_at)}</div>
                </Col>
                <Col sm={6}>
                  <small className="text-muted">Durée :</small>
                  <div>{cashSessionService.formatDuration(session.duration_minutes)}</div>
                </Col>
              </Row>
              <hr className="my-2" />

              <Table size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <td>Nombre de ventes</td>
                    <td className="text-end"><strong>{session.total_sales_count || 0}</strong></td>
                  </tr>
                  {session.total_cancelled_count > 0 && (
                    <tr className="text-muted">
                      <td>Ventes annulées</td>
                      <td className="text-end">{session.total_cancelled_count}</td>
                    </tr>
                  )}
                  <tr>
                    <td>Chiffre d'affaires</td>
                    <td className="text-end"><strong>{fmt(session.total_revenue)}</strong></td>
                  </tr>
                  {session.total_discount > 0 && (
                    <tr className="text-muted small">
                      <td>Total remises</td>
                      <td className="text-end">{fmt(session.total_discount)}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* === Répartition par méthode === */}
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <strong>💳 Répartition par méthode de paiement</strong>
            </Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" className="mb-0">
                <tbody>
                  {[
                    { label: '💵 Espèces', value: session.total_cash, highlight: true },
                    { label: '📱 Wave', value: session.total_wave },
                    { label: '📲 Orange Money', value: session.total_orange_money },
                    { label: '💳 Carte', value: session.total_card },
                    { label: '🏦 Virement', value: session.total_bank_transfer },
                    { label: '📝 Chèque', value: session.total_check },
                    { label: '⏳ Crédit', value: session.total_credit }
                  ].filter(item => parseFloat(item.value) > 0).map((item, i) => (
                    <tr key={i} className={item.highlight ? 'table-success' : ''}>
                      <td>{item.label}</td>
                      <td className="text-end">
                        <strong>{fmt(item.value)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* === Calcul théorique === */}
          <Card className="mb-3 border-primary">
            <Card.Body>
              <h6 className="mb-3">🧮 Calcul du montant théorique en caisse</h6>
              <Row className="mb-2">
                <Col xs={8}>Fond d'ouverture</Col>
                <Col xs={4} className="text-end">{fmt(session.opening_amount)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={8}>+ Ventes en espèces</Col>
                <Col xs={4} className="text-end">{fmt(session.total_cash)}</Col>
              </Row>
              {session.total_expenses > 0 && (
  <tr className="text-danger">
    <td>
      <span className="me-2">💸</span>
      Sorties caisse (dépenses)
      {session.total_expenses_count > 0 && (
        <small className="text-muted ms-2">
          ({session.total_expenses_count})
        </small>
      )}
    </td>
    <td className="text-end fw-bold text-danger">
      − {cashSessionService.formatCurrency(session.total_expenses)}
    </td>
  </tr>
)}
              <hr className="my-2" />
              <Row className="fw-bold fs-5">
                <Col xs={8}>= Montant théorique :</Col>
                <Col xs={4} className="text-end text-primary">{fmt(theoretical)}</Col>
              </Row>
            </Card.Body>
          </Card>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* === Saisie comptage réel === */}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                <DollarSign size={16} className="me-1" />
                Montant compté en caisse <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="any"
                placeholder="Comptez les espèces dans le tiroir"
                value={closingAmount}
                onChange={(e) => setClosingAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                size="lg"
                autoFocus
                disabled={loading}
              />
            </Form.Group>

            {/* Affichage de l'écart en temps réel */}
            {closingAmount && (
              <Alert
                variant={Math.abs(difference) < 1 ? 'success' : difference < 0 ? 'danger' : 'warning'}
                className="d-flex align-items-center justify-content-between"
              >
                <span>
                  <strong>Écart : </strong>
                  {difference > 0 && '+'}
                  {fmt(difference)}
                </span>
                <span className="small">
                  {Math.abs(difference) < 1 ? '✅ Caisse équilibrée'
                    : difference < 0 ? '⚠️ Manque en caisse'
                    : 'ℹ️ Surplus en caisse'}
                </span>
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Note {forceMode || (difference !== null && Math.abs(difference) >= 1) ? (
                  <span className="text-danger">*</span>
                ) : '(optionnel)'}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={500}
                placeholder={
                  difference < 0
                    ? 'Expliquez la raison du manque...'
                    : difference > 0
                    ? 'Expliquez le surplus...'
                    : 'Note libre (optionnel)'
                }
                value={closingNote}
                onChange={(e) => setClosingNote(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant={forceMode ? 'danger' : 'warning'}
            onClick={handleSubmit}
            disabled={loading || closingAmount === ''}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Fermeture...
              </>
            ) : (
              <>
                <Unlock size={16} className="me-2" />
                {forceMode ? 'Forcer la fermeture' : 'Fermer la session'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // ÉTAPE 2 : Rapport de fermeture (succès)
  // ═══════════════════════════════════════════════════════════
  return (
    <Modal show={show} onHide={handleFinish} backdrop="static" centered size="lg">
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-2">
          <CheckCircle2 size={20} className="text-success" />
          Session fermée
          <Badge bg="secondary" className="ms-2">{closedSession.session_number}</Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="session-report" id="session-report-print">
        <div className="text-center mb-3">
          <h5 className="mb-1">🧾 Rapport de session</h5>
          <p className="text-muted small mb-0">
            Vendeur : <strong>{closedSession.user_name}</strong>
          </p>
        </div>

        <Row className="mb-3">
          <Col sm={6}>
            <small className="text-muted">Ouverture :</small>
            <div>{cashSessionService.formatDateTime(closedSession.opened_at)}</div>
          </Col>
          <Col sm={6}>
            <small className="text-muted">Fermeture :</small>
            <div>{cashSessionService.formatDateTime(closedSession.closed_at)}</div>
          </Col>
        </Row>

        <Table bordered size="sm" className="mb-3">
          <tbody>
            <tr>
              <td>Fond d'ouverture</td>
              <td className="text-end">{fmt(closedSession.opening_amount)}</td>
            </tr>
            <tr>
              <td>Nombre de ventes</td>
              <td className="text-end">{closedSession.total_sales_count}</td>
            </tr>
            <tr>
              <td>Chiffre d'affaires</td>
              <td className="text-end"><strong>{fmt(closedSession.total_revenue)}</strong></td>
            </tr>
          </tbody>
        </Table>

        <h6 className="mb-2">Détail par méthode de paiement</h6>
        <Table bordered size="sm" className="mb-3">
          <tbody>
            {[
              { label: 'Espèces', value: closedSession.total_cash },
              { label: 'Wave', value: closedSession.total_wave },
              { label: 'Orange Money', value: closedSession.total_orange_money },
              { label: 'Carte', value: closedSession.total_card },
              { label: 'Virement', value: closedSession.total_bank_transfer },
              { label: 'Chèque', value: closedSession.total_check },
              { label: 'Crédit', value: closedSession.total_credit }
            ].filter(item => parseFloat(item.value) > 0).map((item, i) => (
              <tr key={i}>
                <td>{item.label}</td>
                <td className="text-end">{fmt(item.value)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Card className="mb-2">
          <Card.Body>
            <Row className="mb-2">
              <Col xs={8}>Montant théorique</Col>
              <Col xs={4} className="text-end">{fmt(closedSession.closing_amount_theoretical)}</Col>
            </Row>
            <Row className="mb-2">
              <Col xs={8}>Montant compté</Col>
              <Col xs={4} className="text-end">{fmt(closedSession.closing_amount_real)}</Col>
            </Row>
            <hr className="my-2" />
            <Row className="fw-bold">
              <Col xs={8}>Écart</Col>
              <Col xs={4} className={`text-end ${
                Math.abs(closedSession.closing_difference) < 1 ? 'text-success'
                  : closedSession.closing_difference < 0 ? 'text-danger' : 'text-warning'
              }`}>
                {closedSession.closing_difference > 0 && '+'}
                {fmt(closedSession.closing_difference)}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {closedSession.closing_note && (
          <Alert variant="light" className="mb-0">
            <small className="text-muted">Note :</small>
            <div>{closedSession.closing_note}</div>
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-primary" onClick={handlePrint}>
          <Printer size={16} className="me-2" />
          Imprimer
        </Button>
        <Button variant="success" onClick={handleFinish}>
          <CheckCircle2 size={16} className="me-2" />
          Terminer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseSessionModal;